import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import useCompositionGuard from "../../hooks/useCompositionGuard";
import TeamPersonnelGroup from "./TeamPersonnelGroup";

/**
 * TeamAssignmentPopover — Compact inline popover for assigning/editing
 * Team Number + Personnel 1 + Personnel 2 directly from a table row.
 *
 * Reuses the same TeamPersonnelGroup logic used by the modal, ensuring
 * identical rules for auto-population, occupied-personnel blocking,
 * and composition-conflict guarding.
 *
 * Props:
 *   anchorRect     — DOMRect from getBoundingClientRect() of the clicked cell
 *   record         — { id, team_number, personnel_1_id, personnel_2_id }
 *   assignmentDate — "YYYY-MM-DD" for composition + occupancy lookup
 *   apiPath        — "/job-orders" or "/migration" (PUT endpoint prefix)
 *   onSave         — (updatedRecord) => void — called after successful save
 *   onClose        — () => void
 */
export default function TeamAssignmentPopover({
  anchorRect,
  record,
  assignmentDate,
  apiPath,
  onSave,
  onClose,
  isHistory = false,
  companyId,
}) {
  const { user } = useAuth();
  // Company whose teams/personnel the pickers should offer. Defaults to the
  // current user's company; callers pass the record's company when a
  // cross-company editor is (re)assigning another company's job order.
  const effectiveCompanyId = companyId ?? user?.company_id;
  const { check: checkComp, ConflictDialog } = useCompositionGuard();
  const popoverRef = useRef(null);

  // Local form state
  const [teamNumber, setTeamNumber] = useState(record?.team_number || "");
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [personnel1Id, setPersonnel1Id] = useState(record?.personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(record?.personnel_2_id || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Flag to suppress outside-click/Escape while the ConflictDialog is showing.
  // The dialog renders outside popoverRef, so clicks on it would otherwise close the popover.
  const busyRef = useRef(false);

  // Resolve team_master_id from team_number on mount
  useEffect(() => {
    if (!record?.team_number) return;
    api
      .get("/team-masters/active", { params: { company_id: effectiveCompanyId } })
      .then(({ data }) => {
        const match = data.find((t) => t.team_number === record.team_number);
        if (match) setSelectedTeamId(match.id);
      })
      .catch(() => {});
  }, [record?.team_number, effectiveCompanyId]);

  // Close on outside click — suppressed while composition-conflict dialog is open
  useEffect(() => {
    const handler = (e) => {
      if (busyRef.current) return;
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [onClose]);

  // Close on Escape — suppressed while composition-conflict dialog is open
  useEffect(() => {
    const handler = (e) => {
      if (busyRef.current) return;
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleTeamChange = useCallback((teamId, teamData) => {
    setSelectedTeamId(teamId);
    setTeamNumber(teamData ? teamData.team_number : "");
  }, []);

  const handleSave = async () => {
    setError("");

    // Composition guard check — busyRef prevents the popover from closing
    // while the ConflictDialog is awaiting user input
    if (teamNumber && personnel1Id) {
      busyRef.current = true;
      const ok = await checkComp({
        teamNumber,
        assignmentDate,
        personnel1Id,
        personnel2Id,
      });
      busyRef.current = false;
      if (!ok) return;
    }

    setSaving(true);
    try {
      // Save via PUT — use history endpoint for rescheduled records
      const url = isHistory
        ? `${apiPath}/history/${record.history_id}/team`
        : `${apiPath}/${record.id}`;
      const { data: updated } = await api.put(url, {
        team_number: teamNumber || null,
        personnel_1_id: personnel1Id || null,
        personnel_2_id: personnel2Id || null,
      });
      onSave(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save team assignment.");
    } finally {
      setSaving(false);
    }
  };

  // Popover positioning — anchored below the clicked cell, repositioned if off-screen
  let top = 0;
  let left = 0;
  if (anchorRect) {
    top = anchorRect.bottom + 4;
    left = anchorRect.left;
    const popoverWidth = 320;
    const popoverHeight = 340;
    if (left + popoverWidth > window.innerWidth) left = window.innerWidth - popoverWidth - 8;
    if (top + popoverHeight > window.innerHeight) top = anchorRect.top - popoverHeight - 4;
    if (left < 4) left = 4;
    if (top < 4) top = 4;
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50" onClick={onClose} />

      {/* Popover */}
      <div
        ref={popoverRef}
        className="bg-white rounded-lg shadow-xl border border-gray-200 w-[310px] z-50"
        style={{ position: "fixed", top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Team Assignment
          </p>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="px-4 py-3">
          <TeamPersonnelGroup
            teamMasterId={selectedTeamId}
            personnel1Id={personnel1Id}
            personnel2Id={personnel2Id}
            onTeamChange={handleTeamChange}
            onPersonnel1Change={(id) => setPersonnel1Id(id)}
            onPersonnel2Change={(id) => setPersonnel2Id(id)}
            companyId={effectiveCompanyId}
            assignmentDate={assignmentDate}
          />

          {error && (
            <p className="text-xs text-red-600 mt-2">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 px-4 pb-3">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-xs text-white rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Composition conflict dialog (from useCompositionGuard) */}
      <ConflictDialog />
    </>
  );
}
